<template>
  <ul
    class="list-unstyled list-separated"
    data-test="publisher-group-list"
  >
    <template v-for="(group) in displayedPublisherGroupsOrdered">
      <li
        :key="`${group.name}-item`"
        class="list-separated-item border-bottom"
        data-test="publisher-group-list-item"
      >
        <div class="d-flex align-items-center">
          <b-form-checkbox
            :id="group.name"
            :name="group.name"
            :checked="isGroupChecked(group.name)"
            @change="updatePublisherGroupState(group.name, $event)"
            size="xl"
            class="publisher-checkbox"
            data-test="publisher-group-list-item-checkbox"
            :disabled="disabled"
          />
          <div
            class="text-inherit pl-3"
            data-test="publisher-group-list-item-name"
          >
            {{ group.name }}
          </div>
          <div
            class="selectgroup-button selectgroup-button-icon ml-auto"
            v-b-toggle="`accordion-${group.id}`"
            :data-test="`${group.name}-collapse-button`"
          >
            <i
              class="when-closed fa far fa-angle-down"
            />
            <i
              class="when-opened fa far fa-angle-up"
            />
          </div>
        </div>
      </li>
      <b-collapse
        class="py-4 pl-5 border-bottom"
        :id="`accordion-${group.id}`"
        :key="group.name"
        accordion="my-accordion"
        role="tabpanel"
      >
        <li
          v-for="publisher in group.publisherIds"
          :key="publisher"
          class="list-separated-item"
          data-test="publisher-group-publisher-list-item"
        >
          <div
            class="row align-items-center"
            v-if="getPublisherDetails(publisher)"
          >
            <div class="col-auto">
              <base-company-logo
                :company="getPublisherDetails(publisher)"
              />
            </div>
            <div class="col">
              <div
                class="text-inherit"
                data-test="publisher-group-publisher-list-item-name"
              >
                {{ getPublisherDetails(publisher).name }}
              </div>
              <small class="d-block item-except text-sm h-1x">
                <a
                  :href="`http://${getPublisherDetails(publisher).domain}`"
                  target="_blank"
                >
                  {{ getPublisherDetails(publisher).domain }}
                </a>
              </small>
            </div>
            <div class="col text-right">
              <label
                class="custom-switch-label"
              >
                <input
                  type="checkbox"
                  :name="`${group.name}-${getPublisherDetails(publisher).id}`"
                  class="custom-switch-input"
                  :checked="checkedPublisherIds.includes(getPublisherDetails(publisher).id)"
                  @click="updatePublisherState(group.name, getPublisherDetails(publisher).id, $event)"
                  :disabled="disabled || !checkedGroupNames.includes(group.name)"
                >
                <span
                  ref="custom-switch-indicator"
                  class="custom-switch-indicator"
                />
              </label>
            </div>
          </div>
        </li>
      </b-collapse>
    </template>
  </ul>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';
import * as types from '@/modules/campaigns/store/types';
import BaseCompanyLogo from '@/components/BaseCompanyLogo.vue';
import helper from '@/core/helper';
import suppressibleBox from '@/core/mixins/suppressibleBox';
import arrayHelper from '@/core/mixins/arrayHelper';
import { noop } from 'lodash';

export default {
  name: 'GroupPublisherSelection',
  mixins: [
    arrayHelper,
    suppressibleBox,
  ],
  components: { BaseCompanyLogo },
  props: {
    publishers: {
      type: Array,
      default: () => [],
    },
    publisherGroups: {
      type: Array,
      default: () => [],
    },
    displayedPublisherGroups: {
      type: Array,
      default: () => [],
    },
    selectedPublisherIds: {
      type: Array,
      default: () => [],
    },
    selectedPublisherGroups: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
    },
  },
  data() {
    return {
      isLoading: false,
      checkedGroupNames: [],
      checkedPublisherIds: [],
    };
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
    ]),
    selectedGroups() {
      return this.displayedPublisherGroups.filter((group) => this.selectedPublisherGroups.includes(group.name));
    },
    selectedGroupIds() {
      return this.selectedGroups.map((group) => group.id);
    },
    notSelectedGroups() {
      return this.displayedPublisherGroups.filter((group) => !this.selectedPublisherGroups.includes(group.name));
    },
    displayedPublisherGroupsOrdered() {
      const { selectedGroups } = this;
      const { notSelectedGroups } = this;
      return [...selectedGroups.length > 0 ? helper.orderBy(selectedGroups, 'name') : [],
        ...notSelectedGroups.length > 0 ? helper.orderBy(notSelectedGroups, 'name') : []];
    },
  },
  methods: {
    ...mapActions('campaigns', [
      types.ADD_PUBLISHER,
    ]),
    ...mapMutations('campaigns', [
      types.CLEAR_PUBLISHERS,
    ]),
    getGroup(groupName) {
      return this.publisherGroups.find((group) => group.name === groupName);
    },
    getPublisherDetails(id) {
      return this.publishers.find((publisher) => Number(publisher.id) === Number(id));
    },
    isGroupChecked(groupName) {
      return this.checkedGroupNames.includes(groupName);
    },
    updatePublisherGroupState(groupName, toggleState) {
      const group = this.publisherGroups.find((g) => g.name === groupName);
      if (toggleState) {
        this.addPublisherGroup(group);
      } else {
        this.removePublisherGroup(group);
      }
      this.$emit('updateSelectedGroupIds', this.selectedGroupIds);
      this.$emit('updateSelectedGroups', this.checkedGroupNames);
    },
    addPublisherGroup(group) {
      this.addElementToArrayIfNotPresent(group.name, this.checkedGroupNames);
      group.publisherIds.forEach((publisherId) => {
        this.addElementToArrayIfNotPresent(publisherId, this.checkedPublisherIds);
      });
    },
    removePublisherGroup(group) {
      this.removeElementFromArray(group.name, this.checkedGroupNames);
      group.publisherIds.forEach((publisherId) => {
        if (!this.isPublisherPresentInAlreadyTickedGroups(publisherId)) {
          this.removeElementFromArray(publisherId, this.checkedPublisherIds);
        }
      });
    },
    isPublisherPresentInAlreadyTickedGroups(publisherId) {
      return this.checkedGroupNames.map((groupName) => this.getGroup(groupName))
        .filter((group) => group.publisherIds.indexOf(publisherId) !== -1).length > 0;
    },
    updatePublisherState(groupName, publisherId, clickEvent) {
      const isChecked = clickEvent.target.checked;
      if (isChecked) {
        this.addElementToArrayIfNotPresent(publisherId, this.checkedPublisherIds);
      } else {
        this.displayRemovePublisherSuppressMsg();
        this.removeElementFromArray(publisherId, this.checkedPublisherIds);
      }
      this.$emit('updateSelectedPublisherIds');
    },
    displayRemovePublisherSuppressMsg() {
      this.boxOk(
        'Warning!',
        'This publisher will be removed from all selected verticals.',
        `suppressRemovePublisherMsgFor${this.campaign.id}`,
      ).then(() => noop());
    },
  },
  beforeDestroy() {
    this.$emit('updateSelectedGroupIds', []);
  },
  created() {
    this.checkedGroupNames = this.selectedPublisherGroups;
    this.checkedPublisherIds = this.selectedPublisherIds;
    this.$emit('updateSelectedGroupIds', this.selectedGroupIds);
  },
};
</script>
<style scoped lang="scss">
::v-deep {
  div[id*='accordion'] {
    transition: height 0.2s ease-in-out;
  }
}
.custom-switch-input:disabled ~ .custom-switch-indicator {
  opacity: 0.3;
  cursor: not-allowed;
}
</style>
